<template>
	<DataTable
		:value="rows"
		:loading="loading"
		class="p-datatable-sm"
		:autoLayout="true"
		columnResizeMode="fit"
		v-model:expandedRows="expandedRows"
		@row-dblclick="openEditDialog($event.data)"
		:paginator="true"
		:rows="tableListPagination.pageSize"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}  '"
		:scrollable="true"
		scrollHeight="flex"
		@page="onChangePage"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.DocumentClasses.ExportInterfaces') }) }}
		</template>

		<Column :expander="true" headerStyle="width: 3rem" style="min-width: 4rem; max-width: 4rem;" />
		<Column field="id" header="ID" :sortable="false" filterMatchMode="contains" style="min-width: 4rem; max-width: 4rem;" />
		<Column field="interfaceName" :header="$t('Squeeze.DocumentClasses.Type')" :sortable="false" filterMatchMode="contains" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="getExportInterfaceTypeName(slotProps.data.interfaceName)">
					{{ getExportInterfaceTypeName(slotProps.data.interfaceName) }}
				</div>
			</template>
		</Column>

		<Column field="description" :header="$t('Squeeze.DocumentClasses.Description')" :sortable="false" filterMatchMode="contains" style="min-width: 10rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.description">
					{{slotProps.data.description}}
				</div>
			</template>
		</Column>
		<Column field="active" :header="$t('Squeeze.DocumentClasses.Active')" :sortable="false" filterMatchMode="contains" headerClass="header-center" style="min-width: 6rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<Checkbox @change="onChangeCheckbox(slotProps.data)"  v-model="slotProps.data.active" :binary="true" />
			</template>
		</Column>
		<!-- Delete & Edit -->
		<Column style="min-width: 7rem; max-width: 7rem;">
			<template #body="slotProps">
				<div class="p-d-flex p-justify-end p-ai-center">
					<Button :label="$t('Squeeze.Locators.Testing')" class="p-button p-mr-2" @click="testExportInterface(slotProps.data)" />
					<Button v-tooltip="$t('Squeeze.DocumentClasses.ShowExportStates')" icon="mdi mdi-table-eye" class="p-button-rounded p-button-text p-mr-2" @click="openExportStates(slotProps.data)" />
					<Button v-tooltip="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button-rounded p-button-success p-button-text p-mr-2" @click="openEditDialog(slotProps.data)" />
					<Button v-tooltip="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button-lg p-button-rounded p-button-danger p-button-text" @click="openDeleteDialog(slotProps.data)" />
				</div>
			</template>
		</Column>
		<template #expansion="slotProps">
			<table class="exportInterfaceExpansionTable p-ml-sm-0 p-ml-md-6">
				<template v-for="(property, index) in getExportInterfaceProperties(slotProps.data.interfaceName)" v-bind:key="index">
					<tr class="p-p-2">
						<td class="p-text-bold p-p-2">
							{{property.name}}:
						</td>
						<td>
							<template v-if="property.type === 'string'">
								{{slotProps.data.configuration[property.id]}}
							</template>
							<template v-else-if="property.type === 'password'">
								*****
							</template>
							<template v-else-if="property.type === 'select'">
								{{ getSelectedValueName(property.options, slotProps.data.configuration[property.id]) }}
							</template>
							<template v-else-if="property.type === 'sequenceFromSet'">
								<template v-if="!slotProps.data.configuration[property.id] || !slotProps.data.configuration[property.id].length">-</template>
								<template v-else>
									{{ slotProps.data.configuration[property.id].join('/') }}
								</template>
							</template>
							<template v-else>
								{{slotProps.data.configuration[property.id]}}
							</template>
						</td>
					</tr>
				</template>
			</table>
		</template>

		<template #paginatorstart>
			<Button :label="$t('Squeeze.General.NewEntry')" type="button" icon="mdi mdi-plus" class="p-button" @click="openNewEntry()" />
		</template>
		<template #paginatorend></template>
	</DataTable>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import useSqueezeStore from "@/apps/squeeze/store";
import {useRoute} from "vue-router";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import router from "@/router";
import Tooltip from "primevue/tooltip";
import {ExportInterfaceDocumentationDto, ExportInterfaceDto, ExportInterfacePropertyDocumentationDto} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {TableListSettings, TableSettings} from "@/util/TableSettings";

export default defineComponent({
	name: 'DocumentClassExportInterface',
	components: {
		DataTable,
		Column,
		Checkbox,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** All rows */
		rows: {
			type: Array as PropType<ExportInterfaceDto[]>,
			default: () => [],
		},
		/** Currently active document-class */
		documentClassId: {
			type: Number,
			default: 0,
		},
		allExportInterfaces: {
			type: Array as PropType<ExportInterfaceDocumentationDto[]>,
			default: () => [],
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: ["openEntryDialog", "deleteEntry", "testExportInterface", "onChangeCheckbox"],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** A single item (used for insert/edit/delete */
		const rowData = reactive<ExportInterfaceDto>({interfaceName: "", configuration: {}});

		/** Array with the expanded rows-data */
		const expandedRows = ref<any>([]);

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/**
		 * Opens the Delete-Dialog
		 * @param {ExportInterfaceDto} row The row to delete
		 */
		const openDeleteDialog = (row: ExportInterfaceDto) => {
			Object.assign(rowData, row);
			emit("deleteEntry", rowData);
		}

		/** Create a new entry */
		const openNewEntry = () => {
			emit("openEntryDialog", { id: undefined, active: true, description: '', configuration: undefined, interfaceName: ''})
		}

		/**
		 * Opens the Edit-Dialog
		 * @param {ExportInterfaceDto} row The Row to edit
		 */
		const openEditDialog = (row: ExportInterfaceDto) => {
			Object.assign(rowData, row);
			emit("openEntryDialog", rowData);
		}

		/**
		 * Gets the properties for an Export-Interface
		 * @param {string} name Name of the Export-Interface
		 */
		const getExportInterfaceProperties = (name: string) => {
			const exportInterface = props.allExportInterfaces.find(exportInterface => exportInterface.id === name);
			if (exportInterface) {
				return exportInterface.properties;
			}

			return {};
		}

		/**
		 * Test the current Export Interface
		 * @param {ExportInterfaceDto} row Current row
		 */
		const testExportInterface = (row: ExportInterfaceDto) => {
			emit("testExportInterface", row.id)
		}

		/**
		 * Get the name of an export interface type instead of id
		 * @param {string} interfaceId Current export interface id
		 */
		const getExportInterfaceTypeName = (interfaceId: string) => {
			const interfaceData = props.allExportInterfaces.find(exportInterface => exportInterface.id === interfaceId);
			if (interfaceData) {
				return interfaceData.name;
			}

			return interfaceId;
		}

		/**
		 * Gets the selected value from a field and returns the name
		 * @param {ExportInterfacePropertyDocumentationDto} properties Property to get the name from
		 * @param {string} value Value to get the name from
		 */
		const getSelectedValueName = (properties: ExportInterfacePropertyDocumentationDto[], value: string) => {
			const property = properties.find(property => property.id === value);
			if (property) {
				return property.name;
			}

			return value;
		}

		/**
		 * Triggered when a checkbox is clicked
		 * @param {ExportInterfaceDto} data Data to edit
		 */
		const onChangeCheckbox = (data: ExportInterfaceDto) => {
			emit("onChangeCheckbox", data);
		}

		/** Triggered to show the ExportStates
		 * @param {ExportInterfaceDto} data
		 */
		const openExportStates = (data: ExportInterfaceDto) => {
			router.push({ name: 'DocumentClassExportStatesSubView', params: { documentClassId: String(data.documentClassId), exportId: String(data.id) }});
		}

		/**
		 * Triggered when page is changed
		 * @param event
		 */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		})

		return {
			t,
			toast,
			route,
			store,
			rowData,
			expandedRows,
			tableListPagination,
			openDeleteDialog,
			openNewEntry,
			openEditDialog,
			getExportInterfaceProperties,
			testExportInterface,
			getExportInterfaceTypeName,
			getSelectedValueName,
			onChangeCheckbox,
			openExportStates,
			onChangePage,
		};
	},
});

</script>

<style scoped>

.exportInterfaceExpansionTable {
	width: auto;
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>
